/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PHASE_6_BROADCAST_RESPONSE_PAGE
 * Date:         2026-01-28
 * Modified By:  Cursor
 *
 * Description:
 * Phase 6: Broadcast response page for users to respond to broadcast listings (BR-13, BR-16).
 * Handles authentication redirect, form submission, and deleted broadcast detection.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Projected: ~180 lines
 * - Decision: Proceed (within 200 line limit)
 *
 * Related Files:
 * - client/src/services/broadcastService.ts
 * - client/src/pages/Broadcast/BroadcastDeleted.tsx
 * - server/routes/broadcasts.js
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { getBroadcastForResponse, submitBroadcastResponse } from "../../services/broadcastService";
import { notify } from "../../utils/notifications";
import styles from "./Broadcast.module.scss";

interface BroadcastResponseData {
  broadcast: {
    id: string;
    title: string;
    sender: {
      name: string;
      company: string;
    };
  };
  responder: {
    name: string;
    company: string;
    email: string;
    phone: string;
  };
}

function BroadcastResponse() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<BroadcastResponseData | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // [2026-07-08] cookie-auth-fix: removed deprecated getToken() localStorage gate.
    // After the cookie-auth migration, cookie-authenticated users have no localStorage
    // token, so the old gate bounced them to /signin -> "/" (dashboard). The cookie
    // (withCredentials) authenticates the request; the 401 handler below covers the
    // logged-out case (redirect to /signin with returnTo). (BR-16)

    // [2026-01-28] Phase 6: Fetch broadcast details
    async function fetchBroadcast() {
      try {
        setLoading(true);
        setError(null);

        const response = await getBroadcastForResponse(id!);
        setData(response.data);
      } catch (error: any) {
        console.error("Error fetching broadcast:", error);

        if (error.response?.status === 410 || error.response?.status === 404) {
          // [2026-02-23] Handle both 410 (soft-deleted legacy) and 404 (hard-deleted)
          navigate(`/broadcast/${id}/deleted`, {
            state: { suggestions: error.response.data?.suggestions || [] },
          });
        } else if (error.response?.status === 401) {
          // [2026-01-28] Phase 6: Not authenticated
          const returnTo = encodeURIComponent(`/broadcast/${id}/respond`);
          navigate(`/signin?returnTo=${returnTo}`);
        } else {
          setError(
            error.response?.data?.message ||
              "Failed to load broadcast. Please try again."
          );
        }
      } finally {
        setLoading(false);
      }
    }

    fetchBroadcast();
  }, [id, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 10) {
      setError("Message must be at least 10 characters");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitBroadcastResponse(id!, message.trim());
      notify.success("Your response has been sent to the seller");
      navigate("/", {
        state: { message: "Your response has been sent to the seller" },
      });
    } catch (error: any) {
      console.error("Error submitting response:", error);
      setError(
        error.response?.data?.message || "Failed to send response. Please try again."
      );
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading broadcast details...</p>
      </Container>
    );
  }

  if (error && !data) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/")}>
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Container className={`mt-5 ${styles.narrowContainer}`}>
      <h2 className="mb-4">Respond to Broadcast</h2>

      {/* [2026-01-28] Phase 6: Broadcast summary */}
      <div className="mb-4 p-3 bg-light rounded">
        <h4>{data.broadcast.title}</h4>
        <p className="mb-1">
          <strong>Seller:</strong> {data.broadcast.sender.name}
        </p>
        <p className="mb-0">
          <strong>Company:</strong> {data.broadcast.sender.company}
        </p>
      </div>

      {/* [2026-01-28] Phase 6: Responder info */}
      <div className="mb-4 p-3 bg-light rounded">
        <h5>Your Contact Information</h5>
        <p className="mb-1">
          <strong>Name:</strong> {data.responder.name}
        </p>
        <p className="mb-1">
          <strong>Company:</strong> {data.responder.company}
        </p>
        <p className="mb-1">
          <strong>Email:</strong> {data.responder.email}
        </p>
        <p className="mb-0">
          <strong>Phone:</strong> {data.responder.phone}
        </p>
      </div>

      {/* [2026-01-28] Phase 6: Response form */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>
            Your Message <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="I'm interested in this item..."
            required
            minLength={10}
            maxLength={500}
            disabled={submitting}
          />
          <Form.Text className="text-muted">
            Message must be at least 10 characters. {message.length}/500 characters.
          </Form.Text>
        </Form.Group>

        <div className="d-flex gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || message.trim().length < 10}
          >
            {submitting ? "Sending..." : "Send Response"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/")}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </Container>
  );
}

export default BroadcastResponse;
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
